import React, { ReactNode, TextareaHTMLAttributes } from "react";
import { ComponentProps } from "@stitches/react";
import { ChangeHandler } from "react-hook-form";
import { TextareaInput } from "./__styles__/TextInput";
import Wrapper from "./Wrapper";

type StrippedDownInputProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "size" | "value" | "onChange"
>;

interface Props extends StrippedDownInputProps {
  name: string;
  value?: string | number | null;
  label?: Maybe<string>;
  compactLabel?: boolean;
  size?: "smaller" | "small" | "medium";
  helperText?: Maybe<string>;
  error?: string;
  styleOverride?: any;
  placeholder?: string;
  minRows?: number;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  description?: Maybe<string>;
  tooltip?: string;
  isGenAIField?: boolean;
  onRegenerateAIFields?: () => void;
  labelDecorator?: ReactNode;
}

const Textarea = ({
  name,
  value = null,
  onChange,
  label = null,
  compactLabel = false,
  size = "medium",
  helperText,
  error,
  styleOverride = null,
  placeholder,
  minRows = 5,
  required,
  description,
  labelSize,
  tooltip,
  disabled,
  readonly,
  labelDecorator,
}: Props & {
  onChange?: (value: string) => void;
  labelSize?: ComponentProps<typeof Wrapper>["labelSize"];
}) => (
  <Wrapper
    label={label}
    labelSize={labelSize}
    compactLabel={compactLabel}
    name={name}
    error={error}
    required={required}
    description={description}
    tooltip={tooltip}
    helperText={helperText}
    printValue={value?.toString() ?? ""}
    labelDecorator={labelDecorator}
  >
    <TextareaInput
      name={name}
      onChange={evt => onChange?.(evt.target.value)}
      size={size}
      error={!!error}
      style={styleOverride}
      id={name}
      value={value?.toString() ?? ""}
      placeholder={placeholder}
      minRows={minRows}
      maxRows={15}
      isDisabled={disabled}
      isReadonly={readonly}
      disabled={disabled || readonly}
    />
  </Wrapper>
);

export const ReactHookFormTextareaInput = React.forwardRef(
  (
    {
      name,
      value,
      onChange,
      label,
      compactLabel = false,
      size = "medium",
      error,
      styleOverride = null,
      placeholder,
      minRows = 5,
      description,
      helperText,
      required,
      ...props
    }: Omit<ComponentProps<typeof TextareaInput>, "error"> &
      Omit<Props, "value"> & { onChange?: ChangeHandler },
    ref: React.Ref<HTMLTextAreaElement>,
  ) => {
    return (
      <Wrapper
        label={label}
        compactLabel={compactLabel}
        name={name}
        error={error}
        printValue={value?.toString() ?? ""}
        description={description}
        helperText={helperText}
        required={required}
      >
        <TextareaInput
          name={name}
          onChange={onChange}
          size={size}
          error={!!error}
          style={styleOverride}
          id={name}
          placeholder={placeholder}
          minRows={minRows}
          maxRows={15}
          inputRef={ref as React.RefObject<HTMLTextAreaElement>}
          {...props}
        />
      </Wrapper>
    );
  },
);

export default Textarea;
